import { orgUiPath } from '@/core/api';
import type { MeResponse } from '@/types/api';
import { isPlatformRoleUser } from '@/utils/platformRole';

/** Query param on ``/login`` and ``/choose-org`` for post-auth navigation. */
export const RETURN_QUERY = 'next';

/** Open the legacy editor renders side panel (``oldeditor`` route only). */
export const PANEL_QUERY = 'panel';
export const PANEL_RENDERS = 'renders';

/** Breadcrumb context when opening a creative from a campaign (shareable). */
export const CAMPAIGN_QUERY = 'campaign';

const LEGACY_TOP_LEVEL = new Set([
  'formats',
  'campaigns',
  'creatives',
  'renders',
  'assets',
  'analytics',
]);

/**
 * Return a same-origin relative path safe to use after login, or ``null``.
 *
 * Rejects protocol-relative URLs, absolute URLs, and off-origin paths.
 */
export function safeReturnPath(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return null;
  if (trimmed.includes('://')) return null;
  if (trimmed.includes('\\')) return null;
  return trimmed;
}

/** Build ``/login?next=…`` when ``returnTo`` is safe. */
export function buildLoginPath(returnTo?: string | null): string {
  const safe = safeReturnPath(returnTo);
  if (!safe) return '/login';
  return `/login?${RETURN_QUERY}=${encodeURIComponent(safe)}`;
}

/** Build ``/choose-org?next=…`` when ``returnTo`` is safe. */
export function buildChooseOrgPath(returnTo?: string | null): string {
  const safe = safeReturnPath(returnTo);
  if (!safe) return '/choose-org';
  return `/choose-org?${RETURN_QUERY}=${encodeURIComponent(safe)}`;
}

/** True when the path is a pre-migration flat app route (no ``/org/{id}`` prefix). */
export function isLegacyAppPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  if (path.startsWith('/org/')) return false;
  if (path === '/login' || path === '/choose-org' || path === '/') return false;
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return false;
  const head = segments[0];
  if (head === 'organizations') return true;
  return LEGACY_TOP_LEVEL.has(head);
}

/** True when the path uses the API-style ``/organizations/{id}`` UI prefix. */
export function isOrganizationsUiPath(pathname: string): boolean {
  return /^\/organizations\/\d+(\/|$)/.test(normalizePathname(pathname));
}

function normalizePathname(pathname: string): string {
  const base = pathname.split('?')[0]?.split('#')[0] ?? '/';
  if (base.length > 1 && base.endsWith('/')) return base.slice(0, -1);
  return base || '/';
}

function splitPathAndSuffix(path: string): { pathname: string; suffix: string } {
  const hashIdx = path.indexOf('#');
  const queryIdx = path.indexOf('?');
  const cut =
    hashIdx === -1
      ? queryIdx === -1
        ? path.length
        : queryIdx
      : queryIdx === -1
        ? hashIdx
        : Math.min(hashIdx, queryIdx);
  const pathname = path.slice(0, cut) || '/';
  const suffix = path.slice(cut);
  return { pathname, suffix };
}

/**
 * Rewrite legacy flat paths to canonical ``/org/{orgId}/…`` routes.
 *
 * Preserves query string and hash on ``rawPath`` when present.
 */
export function migrateLegacyPath(rawPath: string, orgId: string | number): string {
  const safe = safeReturnPath(rawPath);
  if (!safe) return orgUiPath(orgId, '/');

  const { pathname, suffix } = splitPathAndSuffix(safe);
  const migrated = migrateLegacyPathname(pathname, orgId);
  return `${migrated}${suffix}`;
}

/** Map a legacy pathname (no query/hash) to ``/org/{orgId}/…``. */
export function migrateLegacyPathname(pathname: string, orgId: string | number): string {
  const path = normalizePathname(pathname);

  const orgApi = path.match(/^\/organizations\/(\d+)(\/.*)?$/);
  if (orgApi) {
    const rest = orgApi[2] ?? '';
    return `/org/${orgApi[1]}${rest || ''}`;
  }

  const campaignCreative = path.match(/^\/campaigns\/(\d+)\/creatives\/(\d+)$/);
  if (campaignCreative) {
    const [, campaignId, creativeId] = campaignCreative;
    return `${orgUiPath(orgId, `/creatives/${creativeId}`)}?${CAMPAIGN_QUERY}=${campaignId}`;
  }

  const creativeOldEditor = path.match(/^\/creatives\/(\d+)\/oldeditor$/);
  if (creativeOldEditor) {
    return orgUiPath(orgId, `/creatives/${creativeOldEditor[1]}/oldeditor`);
  }

  const creative = path.match(/^\/creatives\/(\d+)$/);
  if (creative) {
    return orgUiPath(orgId, `/creatives/${creative[1]}`);
  }

  const campaign = path.match(/^\/campaigns\/(\d+)$/);
  if (campaign) {
    return orgUiPath(orgId, `/campaigns/${campaign[1]}`);
  }

  const asset = path.match(/^\/assets\/(\d+)$/);
  if (asset) {
    return orgUiPath(orgId, `/assets/${asset[1]}`);
  }

  const top = path.match(/^\/(formats|campaigns|creatives|renders|assets|analytics)$/);
  if (top) {
    return orgUiPath(orgId, `/${top[1]}`);
  }

  const renderDeep = path.match(/^\/renders\/(\d+)$/);
  if (renderDeep) {
    return orgUiPath(orgId, `/renders/${renderDeep[1]}`);
  }

  const creativeRenders = path.match(/^\/creatives\/(\d+)\/renders(?:\/(\d+))?$/);
  if (creativeRenders) {
    const [, creativeId, renderId] = creativeRenders;
    if (renderId) {
      return orgUiPath(orgId, `/creatives/${creativeId}/renders/${renderId}`);
    }
    return orgUiPath(orgId, `/creatives/${creativeId}/renders`);
  }

  return orgUiPath(orgId, '/');
}

/** Parse ``?campaign=`` from a location search string. */
export function parseCampaignIdFromSearch(search: string): number | null {
  const raw = new URLSearchParams(search).get(CAMPAIGN_QUERY);
  if (raw == null || raw.trim() === '') return null;
  const id = Number.parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
}

/** Parse ``?panel=`` from a location search string. */
export function parsePanelFromSearch(search: string): string | null {
  return new URLSearchParams(search).get(PANEL_QUERY);
}

/** Read ``?next=`` from a location search string (login / choose-org). */
export function readReturnQuery(search: string): string | null {
  return safeReturnPath(new URLSearchParams(search).get(RETURN_QUERY));
}

/**
 * After login or org selection, resolve where to navigate.
 *
 * ``pendingReturn`` wins when safe; legacy paths are migrated using ``me``.
 */
export function resolveAuthenticatedDestination(
  me: MeResponse,
  pendingReturn?: string | null,
): string {
  const safe = safeReturnPath(pendingReturn);
  if (!safe) {
    const soleOrgId = pickSingleAccessibleOrg(me);
    if (soleOrgId != null) return orgUiPath(soleOrgId, '/');
    if (me.organizations.length > 0) return '/choose-org';
    return '/choose-org';
  }

  const { pathname } = splitPathAndSuffix(safe);

  if (isLegacyAppPath(pathname) || isOrganizationsUiPath(pathname)) {
    const soleOrgId = pickSingleAccessibleOrg(me);
    if (soleOrgId != null) {
      return migrateLegacyPath(safe, soleOrgId);
    }
    return buildChooseOrgPath(safe);
  }

  const orgMatch = pathname.match(/^\/org\/(\d+)(\/|$)/);
  if (orgMatch) {
    const orgId = orgMatch[1];
    const allowed = me.organizations.some((o) => o.id != null && String(o.id) === orgId);
    if (!allowed) return buildChooseOrgPath(safe);
    return safe;
  }

  return safe;
}

/** Org id to use when migrating a legacy path, or ``null`` when chooser is required. */
export function pickOrgForLegacyRedirect(me: MeResponse): number | null {
  return pickSingleAccessibleOrg(me);
}

function pickSingleAccessibleOrg(me: MeResponse): number | null {
  if (isPlatformRoleUser(me.user)) return null;
  if (me.organizations.length !== 1) return null;
  const org = me.organizations[0];
  return org.id ?? null;
}
